import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Flex, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { fetchGroupRows } from '../../api/endpointsBackend';
import { formatAmount } from '../../utils/goals';
import { formatDayShort } from '../../utils/intlFormatters';

const PAGE = 10;

const EFFECT_STYLE = {
  add: { sign: '+', color: 'redesign.amount.income' },
  subtract: { sign: '−', color: 'redesign.amount.expense' },
  none: { sign: '', color: 'redesign.text.muted' },
  invalid: { sign: '', color: 'redesign.text.muted' },
};

function accountLabel(movement) {
  if (movement.type === 'Transfer') {
    return [movement.from_account?.name, movement.to_account?.name].filter(Boolean).join(' → ');
  }
  return movement.account?.name || '';
}

/**
 * A goal's movements, newest first, each signed by what it does to the goal
 * rather than by its type: a transfer into the goal's account reads +, the
 * card purchase that spent it reads −. A movement the rules no longer count
 * keeps its place, faded, with the reason on hover.
 */
export default function GoalMovementsList({ goalId, refreshKey, onSelect }) {
  const { t } = useTranslation('goals');
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (next = null) => {
    setLoading(true);
    try {
      const data = await fetchGroupRows({
        filters: [{ entity: 'Goal', column: 'id', operator: 'in', values: [goalId] }],
        orderBy: 'date_desc',
        limit: PAGE,
        cursor: next,
      });
      setRows(prev => (next ? [...prev, ...data.items] : data.items));
      setCursor(data.next);
    } catch {
      if (!next) setRows([]);
    } finally {
      setLoading(false);
    }
  }, [goalId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading && rows.length === 0) {
    return <Flex justify="center" py={4}><Spinner size="sm" /></Flex>;
  }
  if (rows.length === 0) {
    return <Text fontSize="sm" color="redesign.text.muted" py={3}>{t('movements.none')}</Text>;
  }

  return (
    <Box>
      {rows.map((movement) => {
        const effect = movement.goal?.effect || 'none';
        const style = EFFECT_STYLE[effect] || EFFECT_STYLE.none;
        const reason = movement.goal?.reason;
        return (
          <Flex
            key={movement.id}
            align="center"
            justify="space-between"
            gap={3}
            h="38px"
            px={1}
            mx={-1}
            borderRadius="md"
            cursor="pointer"
            _hover={{ bg: 'redesign.bg.hover' }}
            opacity={effect === 'invalid' ? 0.7 : 1}
            onClick={() => onSelect(movement.id)}
          >
            <Flex align="center" gap={3} minW={0}>
              <Text fontSize="xs" color="redesign.text.muted" w="52px" flexShrink={0}>{formatDayShort(movement.date)}</Text>
              <Text fontSize="sm" color="redesign.heading" isTruncated>{movement.note || movement.description || '—'}</Text>
              <Text fontSize="xs" color="redesign.text.muted" isTruncated flexShrink={1}>{accountLabel(movement)}</Text>
            </Flex>
            <Flex align="center" gap={1.5} flexShrink={0}>
              {effect === 'invalid' && (
                <Tooltip label={t(`reasons.${reason}`, { defaultValue: reason })} hasArrow openDelay={200}>
                  <WarningIcon color="redesign.accent.amber" boxSize={3} />
                </Tooltip>
              )}
              <Text fontSize="sm" fontWeight={500} color={style.color} whiteSpace="nowrap">
                <Text as="span" fontSize="2xs" color="redesign.text.muted" mr={1}>{movement.currency}</Text>
                {style.sign}{formatAmount(movement.amount)}
              </Text>
            </Flex>
          </Flex>
        );
      })}
      {cursor && (
        <Button size="xs" variant="ghost" mt={1} onClick={() => load(cursor)} isLoading={loading} color="redesign.text.muted">
          {t('movements.showMore')}
        </Button>
      )}
    </Box>
  );
}
